import React from 'react';
import styled, {
  FlattenSimpleInterpolation,
  css,
} from 'styled-components/macro';
import {
  FacebookIcon,
  DealerRaterIcon,
  InstagramIcon,
  XIcon,
  YelpIcon,
  TikTokIcon,
  PinterestIcon,
  WhatsappIcon,
  LinkedinIcon,
  BniIcon,
  YoutubeIcon,
  ZeeMeeIcon,
  EdmundsIcon,
  CarsComIcon,
  BBBIcon,
} from './assets';

export enum LP_DESIGN_SOCIAL_MEDIA {
  WHITE_SQUARE = 'white_square',
  BLACK_SQUARE = 'black_square',
  WHITE_CIRCLE = 'white_circle',
  BLACK_CIRCLE = 'black_circle',
  COLORS_SQUARE = 'color_square',
  COLORS_CIRCLE = 'color_circle',
  WHITE = 'white_none',
  BLACK = 'black_none',
}

export type IconType =
  | 'x'
  | 'dealerrater'
  | 'instagram'
  | 'facebook'
  | 'yelp'
  | 'tiktok'
  | 'pinterest'
  | 'whatsapp'
  | 'linkedin'
  | 'bni'
  | 'youtube'
  | 'edmunds'
  | 'zeemee'
  | 'twitter'
  | 'carsdotcom'
  | 'bbb';

const ICON_MAPPER: Record<IconType, JSX.Element> = {
  facebook: <FacebookIcon />,
  dealerrater: <DealerRaterIcon />,
  instagram: <InstagramIcon />,
  x: <XIcon />,
  yelp: <YelpIcon />,
  tiktok: <TikTokIcon />,
  pinterest: <PinterestIcon />,
  whatsapp: <WhatsappIcon />,
  linkedin: <LinkedinIcon />,
  bni: <BniIcon />,
  youtube: <YoutubeIcon />,
  edmunds: <EdmundsIcon />,
  zeemee: <ZeeMeeIcon />,
  carsdotcom: <CarsComIcon />,
  twitter: <XIcon />,
  bbb: <BBBIcon />,
};

export const palette = {
  charcoal_gray: '#212529',
  crimson: '#FF1B1A',
  black: '#000',
  white: '#fff',
  gray20: '#EEEFF2',
  charcoal: '#333333',
  charcoal_brown: 'rgba(28, 28, 27, 1)',
  linkedin_blue: '#007EBB',
  sky_blue: '#007EE5',
  royal_purple: '#65299D',
  cherry_red: '#D11C2F',
  pinterest_red: '#FF0000',
  youtube_red: '#FF0209',
  whatsapp_green: '#00E510',
  orange_sunset: '#F16822',
  transparent: 'transparent',
  facebookBlue: '#1877f2',
};

const ICON_BACKGROUND_MAP: Record<IconType, string> = {
  facebook: palette.facebookBlue,
  dealerrater: palette.white,
  instagram:
    'linear-gradient(45deg, #FAAD4F 14.62%, #DD2A7B 39.39%, #9537B0 58.5%, #515BD4 85.4%)',
  x: palette.black,
  twitter: palette.black,
  yelp: palette.crimson,
  tiktok: palette.charcoal_brown,
  pinterest: palette.pinterest_red,
  whatsapp: palette.whatsapp_green,
  linkedin: palette.linkedin_blue,
  bni: palette.white,
  youtube: palette.youtube_red,
  edmunds: palette.sky_blue,
  zeemee: palette.orange_sunset,
  carsdotcom: palette.royal_purple,
  bbb: palette.white,
};

const COMMON_DESIGN_STYLES: Record<
  LP_DESIGN_SOCIAL_MEDIA,
  FlattenSimpleInterpolation
> = {
  [LP_DESIGN_SOCIAL_MEDIA.WHITE_SQUARE]: css`
    background: ${palette.white};
    border-radius: 4px;
  `,
  [LP_DESIGN_SOCIAL_MEDIA.BLACK_SQUARE]: css`
    background: ${palette.black};
    border-radius: 4px;
  `,
  [LP_DESIGN_SOCIAL_MEDIA.COLORS_SQUARE]: css`
    background: ${palette.black};
    border-radius: 4px;
  `,
  [LP_DESIGN_SOCIAL_MEDIA.WHITE_CIRCLE]: css`
    background: ${palette.white};
    border-radius: 32px;
  `,
  [LP_DESIGN_SOCIAL_MEDIA.BLACK_CIRCLE]: css`
    background: ${palette.black};
    border-radius: 32px;
  `,
  [LP_DESIGN_SOCIAL_MEDIA.COLORS_CIRCLE]: css`
    background: ${palette.black};
    border-radius: 32px;
  `,
  [LP_DESIGN_SOCIAL_MEDIA.WHITE]: css`
    background: ${palette.transparent};
  `,
  [LP_DESIGN_SOCIAL_MEDIA.BLACK]: css``,
};

const createIconStyle = (fillColor?: string, strokeColor?: string) => css`
  svg path {
    ${fillColor ? `fill: ${fillColor};` : ''}
    ${strokeColor ? `stroke: ${strokeColor};` : ''}
  }
`;

const commonColors = {
  white: palette.white,
  transparent: palette.transparent,
  charcoal_gray: palette.charcoal_gray,
  cherryRed: palette.cherry_red,
  black: palette.black,
};

const commonStyles = {
  [LP_DESIGN_SOCIAL_MEDIA.WHITE_SQUARE]: createIconStyle(
    commonColors.charcoal_gray
  ),
  [LP_DESIGN_SOCIAL_MEDIA.BLACK_SQUARE]: createIconStyle(commonColors.white),
  [LP_DESIGN_SOCIAL_MEDIA.WHITE_CIRCLE]: createIconStyle(
    commonColors.charcoal_gray
  ),
  [LP_DESIGN_SOCIAL_MEDIA.BLACK_CIRCLE]: createIconStyle(commonColors.white),
  [LP_DESIGN_SOCIAL_MEDIA.COLORS_SQUARE]: createIconStyle(commonColors.white),
  [LP_DESIGN_SOCIAL_MEDIA.COLORS_CIRCLE]: createIconStyle(commonColors.white),
  [LP_DESIGN_SOCIAL_MEDIA.WHITE]: createIconStyle(
    commonColors.white,
    undefined
  ),
  [LP_DESIGN_SOCIAL_MEDIA.BLACK]: createIconStyle(commonColors.charcoal_gray),
};

const ICON_STYLES: Record<
  IconType,
  Record<LP_DESIGN_SOCIAL_MEDIA, FlattenSimpleInterpolation>
> = {
  dealerrater: {
    ...commonStyles,
    [LP_DESIGN_SOCIAL_MEDIA.COLORS_SQUARE]: createIconStyle(
      commonColors.charcoal_gray
    ),
    [LP_DESIGN_SOCIAL_MEDIA.COLORS_CIRCLE]: createIconStyle(
      commonColors.charcoal_gray
    ),
  },
  instagram: {
    ...commonStyles,
    [LP_DESIGN_SOCIAL_MEDIA.BLACK_CIRCLE]: createIconStyle(commonColors.black),
    [LP_DESIGN_SOCIAL_MEDIA.BLACK_SQUARE]: createIconStyle(
      undefined,
      commonColors.white
    ),
    [LP_DESIGN_SOCIAL_MEDIA.COLORS_CIRCLE]: createIconStyle(
      undefined,
      commonColors.white
    ),
    [LP_DESIGN_SOCIAL_MEDIA.COLORS_SQUARE]: createIconStyle(
      undefined,
      commonColors.white
    ),
    [LP_DESIGN_SOCIAL_MEDIA.BLACK]: createIconStyle(
      undefined,
      commonColors.charcoal_gray
    ),
    [LP_DESIGN_SOCIAL_MEDIA.WHITE]: createIconStyle(
      commonColors.transparent,
      commonColors.white
    ),
  },
  bni: {
    ...commonStyles,
    [LP_DESIGN_SOCIAL_MEDIA.COLORS_SQUARE]: createIconStyle(
      commonColors.cherryRed
    ),
    [LP_DESIGN_SOCIAL_MEDIA.COLORS_CIRCLE]: createIconStyle(
      commonColors.cherryRed
    ),
  },
  zeemee: {
    ...commonStyles,
    [LP_DESIGN_SOCIAL_MEDIA.COLORS_CIRCLE]: createIconStyle(undefined),
    [LP_DESIGN_SOCIAL_MEDIA.COLORS_SQUARE]: createIconStyle(undefined),
    [LP_DESIGN_SOCIAL_MEDIA.BLACK_SQUARE]: css`
      ${createIconStyle(
        commonColors.white,
        undefined
      )} svg path:nth-of-type(2) {
        fill: ${commonColors.charcoal_gray};
      }
    `,
    [LP_DESIGN_SOCIAL_MEDIA.BLACK_CIRCLE]: css`
      ${createIconStyle(
        commonColors.white,
        undefined
      )} svg path:nth-of-type(2) {
        fill: ${commonColors.charcoal_gray};
      }
    `,
    [LP_DESIGN_SOCIAL_MEDIA.WHITE_SQUARE]: css`
      ${createIconStyle(
        commonColors.charcoal_gray,
        undefined
      )} svg path:nth-of-type(2) {
        fill: ${commonColors.white};
      }
    `,
    [LP_DESIGN_SOCIAL_MEDIA.WHITE_CIRCLE]: css`
      ${createIconStyle(
        commonColors.charcoal_gray,
        undefined
      )} svg path:nth-of-type(2) {
        fill: ${commonColors.white};
      }
    `,
    [LP_DESIGN_SOCIAL_MEDIA.BLACK]: css`
      ${createIconStyle(
        commonColors.charcoal_gray,
        undefined
      )} svg path:nth-of-type(2) {
        fill: ${commonColors.white};
      }
    `,
    [LP_DESIGN_SOCIAL_MEDIA.WHITE]: css`
      ${createIconStyle(
        commonColors.white,
        undefined
      )} svg path:nth-of-type(2) {
        fill: ${commonColors.charcoal_gray};
      }
    `,
  },
  carsdotcom: {
    ...commonStyles,
  },
  x: {
    ...commonStyles,
  },
  twitter: {
    ...commonStyles,
  },
  yelp: {
    ...commonStyles,
  },
  tiktok: {
    ...commonStyles,
  },
  pinterest: {
    ...commonStyles,
  },
  whatsapp: {
    ...commonStyles,
  },
  linkedin: {
    ...commonStyles,
  },
  youtube: {
    ...commonStyles,
  },
  edmunds: {
    ...commonStyles,
  },
  facebook: {
    ...commonStyles,
  },
  bbb: {
    ...commonStyles,
  },
};

export const generateIconCss = (
  icon: IconType,
  design: LP_DESIGN_SOCIAL_MEDIA,
  useOutlinedIcon: boolean
) => {
  const commonCss = COMMON_DESIGN_STYLES[design];
  const iconCss = ICON_STYLES[icon][design];

  if (
    design === LP_DESIGN_SOCIAL_MEDIA.COLORS_SQUARE ||
    design === LP_DESIGN_SOCIAL_MEDIA.COLORS_CIRCLE
  ) {
    return css`
      ${commonCss}
      ${iconCss}
      background: ${ICON_BACKGROUND_MAP[icon]};
    `;
  }

  if (useOutlinedIcon && design === LP_DESIGN_SOCIAL_MEDIA.WHITE) {
    return css`
      ${commonCss}
      ${iconCss}
      ${createIconStyle(commonColors.transparent, commonColors.charcoal_gray)}
    `;
  }

  if (
    useOutlinedIcon &&
    (design === LP_DESIGN_SOCIAL_MEDIA.WHITE_SQUARE ||
      design === LP_DESIGN_SOCIAL_MEDIA.WHITE_CIRCLE)
  ) {
    return css`
      ${commonCss}
      ${iconCss}
       border: 1px solid ${palette.gray20};
    `;
  }

  return css`
    ${commonCss}
    ${iconCss}
  `;
};

interface SocialIconProps {
  design: LP_DESIGN_SOCIAL_MEDIA;
  icon: IconType;
  size?: number;
  svgSize?: number;
  useOutlinedIcon?: boolean;
}

export const SocialIcon = ({
  design,
  icon,
  size = 36,
  svgSize = 24,
  useOutlinedIcon = false,
}: SocialIconProps) => {
  const iconCss = generateIconCss(icon, design, useOutlinedIcon);

  const StyledIcon = styled.div`
    ${iconCss}
    height: ${size}px;
    width: ${size}px;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: ${svgSize}px;
      height: ${svgSize}px;
    }
  `;

  return <StyledIcon>{ICON_MAPPER[icon]}</StyledIcon>;
};
